import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, Output, signal, TemplateRef, ViewChild, WritableSignal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  BucketNameValidator,
  BucketsListingRestrictedPossibility,
  e2eLocators
} from '@components/add-destination-form/add-destination-form.constants';
import { CompaniesPickerComponent } from '@components/companies-picker/companies-picker.component';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService, CanPipe } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import {
  AlertComponent,
  ButtonComponent,
  CardComponent,
  CardGroupComponent,
  GroupButtonComponent,
  InputComponent,
  InputLabelComponent,
  MbsPopupType,
  ModalService,
  NumberComponent,
  SelectComponent,
  SwitcherComponent,
  SwitcherDescription
} from 'mbs-ui-kit';
import { PermissionsEnum, StorageType } from 'mbs-ui-shared';
import { amazonS3GlacierStorageTypes, googleCloudStorageTypes } from 'mbs-ui-shared@constants/storage.constants';
import { HelpLinkDirective } from 'mbs-ui-shared@help/helpLink.directive';
import {
  BucketInfo,
  Destination,
  GoogleStorageClass,
  GoogleStorageClassEnum,
  StorageAccountRegion,
  StorageAccountV2
} from 'mbs-ui-shared@models/StorageAccount';
import { StorageAccountsService } from 'mbs-ui-shared@services/storage-accounts/storage-accounts.service';
import { BehaviorSubject, of, switchMap } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, tap } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'mbs-add-destination-form',
  templateUrl: './add-destination-form.component.html',
  styleUrls: ['./add-destination-form.component.scss'],
  imports: [
    ButtonComponent,
    GroupButtonComponent,
    CompaniesPickerComponent,
    CommonModule,
    ReactiveFormsModule,
    NgbTooltipModule,
    CardGroupComponent,
    CardComponent,
    HelpLinkDirective,
    AlertComponent,
    CanPipe,
    I18NextPipe,
    InputLabelComponent,
    InputComponent,
    NumberComponent,
    SelectComponent,
    SwitcherComponent,
    SwitcherDescription
  ]
})
export class AddDestinationFormComponent implements OnInit {
  private storageAccountService = inject(StorageAccountsService);
  private i18N = inject(I18NextPipe);
  ability = inject(AbilityService);
  modalService = inject(ModalService);

  public readonly mbsPopupType = MbsPopupType;
  public readonly googleCloudAccounts = [StorageType.GoogleCloud, StorageType.Google];
  public readonly permissionsEnum = PermissionsEnum;
  public readonly storageType = StorageType;
  public readonly e2eLocators = e2eLocators;
  public readonly Number = Number;

  public storageClasses: GoogleStorageClass[] = null;
  public googleSubregions: StorageAccountRegion[] = null;
  public bucketsLoading = signal(false);
  public bucketLoading = signal(false);
  public currentBucket: WritableSignal<BucketInfo> = signal(null);
  public regions: StorageAccountRegion[];
  public specificGCDataLoading = false;
  public bucketsNames: { bucketName: string }[];
  public bucketsLoaded = false;
  public queries$ = new BehaviorSubject([]);

  public displayNameControl: FormControl;
  public bucketNameControl: FormControl;
  public storageClassControl: FormControl;
  public regionControl: FormControl;
  public googleDestinationSubregionControl: FormControl;
  public objectLockPeriodDaysControl: FormControl;
  public defaultOLControl = new FormControl(false);
  public gfsOLControl: FormControl;
  public companiesControl: FormControl;
  public s3AcceleratorControl: FormControl;
  public reuseAccountRules: FormControl;
  public isExternalBucketControl: FormControl;
  public isWizard = false;

  private get useDraftAccountPath(): boolean {
    return this.isWizard && this.isDraftAccount;
  }
  public _storageAccount: StorageAccountV2 = null;
  @Input() public companiesLocked = false;
  @Input() public selectedCompanyIds: string[] = [];
  // Company-context launch: the destination inherits the context company, so the block is hidden
  @Input() public hideCompanies = false;
  public bucketsListingRestricted: BucketsListingRestrictedPossibility = null;
  public bucketNameRules = [];
  public blockForm = false;
  public manualBucketLoading = false;
  public isUb = false;
  public componentWasInitialized = false;

  private regionField: FormControl;
  private storageClassField: FormControl;

  @Input() form: FormGroup;
  @Input() createNew: FormControl;
  @Input() destination: Destination;
  // bucket, container or root folder
  @Input() bucketTypeName: string;
  @Input() viewMode: 'modal' | 'wizard' = 'modal';
  @Input() isDraftAccount = true;

  @Input() set storageAccount(account: StorageAccountV2) {
    if (!account?.id || account?.id === this._storageAccount?.id) return;

    this.onStorageAccountDefined(account);
  }

  @Output() criticalError = new EventEmitter<string>();
  @Output() currentBucketChanged = new EventEmitter<BucketInfo>();
  @Output() bucketLoadingChanged = new EventEmitter<boolean>();
  @Output() formUnstable = new EventEmitter<boolean>(false);
  @Output() initialLoading = new EventEmitter<boolean>(true);

  @ViewChild('olMismatch', { static: true }) private olMismatch: TemplateRef<any>;

  ngOnInit() {
    this.isWizard = this.viewMode === 'wizard';
    this.queries$.pipe(untilDestroyed(this)).subscribe((val) => this.formUnstable.emit(!!val?.length));
  }

  private setOLControlsSubscriptions() {
    this.defaultOLControl.valueChanges.pipe(untilDestroyed(this)).subscribe((val) => {
      this.objectLockPeriodDaysControl.setValue(val ? 1 : 0, { emitEvent: false });
    });

    this.objectLockPeriodDaysControl.valueChanges.pipe(untilDestroyed(this)).subscribe((val) => {
      this.defaultOLControl.setValue(!!val, { emitEvent: false });
    });
  }

  private setBucketNameValidators() {
    const { storageType } = this._storageAccount;
    const getBucketNames = (key: string): string[] => {
      return Object.values(this.i18N.transform.call(this.i18N, key, { returnObjects: true }));
    };
    const s3LikeStorages = [...amazonS3GlacierStorageTypes, StorageType.S3Compatible, StorageType.Minio, StorageType.IDriveE2];

    if (storageType === StorageType.Azure) {
      this.bucketNameControl.addValidators([
        Validators.pattern(BucketNameValidator.Azure),
        Validators.minLength(3),
        Validators.maxLength(63)
      ]);
      this.bucketNameRules = getBucketNames('shared:modals.addDestinationBucket.bucketNameRules.azure');
    } else if (storageType === StorageType.BackblazeB2) {
      this.bucketNameControl.addValidators([Validators.pattern(BucketNameValidator.B2), Validators.minLength(6), Validators.maxLength(63)]);
      this.bucketNameRules = getBucketNames('shared:modals.addDestinationBucket.bucketNameRules.b2');
    } else if (storageType === StorageType.UbAmazonS3) {
      this.bucketNameControl.addValidators([
        Validators.pattern(BucketNameValidator.UbAws),
        Validators.minLength(1),
        Validators.maxLength(25)
      ]);
      this.bucketNameRules = getBucketNames('shared:modals.addDestinationBucket.bucketNameRules.ubAws');
    } else if (s3LikeStorages.includes(storageType)) {
      this.bucketNameControl.addValidators([Validators.pattern(BucketNameValidator.S3), Validators.minLength(3), Validators.maxLength(63)]);
      this.bucketNameRules = getBucketNames('shared:modals.addDestinationBucket.bucketNameRules.s3');
    } else if (storageType === StorageType.Wasabi || storageType === StorageType.CblWasabi) {
      this.bucketNameControl.addValidators([
        Validators.pattern(BucketNameValidator.Wasabi),
        Validators.minLength(3),
        Validators.maxLength(63)
      ]);
      this.bucketNameRules = getBucketNames('shared:modals.addDestinationBucket.bucketNameRules.wasabi');
    } else if (googleCloudStorageTypes.includes(storageType)) {
      this.bucketNameControl.addValidators([
        Validators.pattern(BucketNameValidator.Google),
        Validators.minLength(3),
        Validators.maxLength(222)
      ]);
      this.bucketNameRules = getBucketNames('shared:modals.addDestinationBucket.bucketNameRules.google');
    } else if (storageType === StorageType.ImpossibleCloud) {
      this.bucketNameControl.addValidators([
        Validators.pattern(BucketNameValidator.ImpossibleCloud),
        Validators.minLength(3),
        Validators.maxLength(63)
      ]);
      this.bucketNameRules = getBucketNames('shared:modals.addDestinationBucket.bucketNameRules.impossibleCloud');
    } else {
      this.bucketNameControl.addValidators([Validators.minLength(1), Validators.maxLength(255)]);
      this.bucketNameRules = getBucketNames('shared:modals.addDestinationBucket.bucketNameRules.common');
    }
  }

  public isManualBucketEntering() {
    return this._storageAccount.isPrivateStorage || this.bucketsListingRestricted || this.isExternalBucketControl.value;
  }

  public showSwitcher() {
    return !this.blockForm && !this._storageAccount.isPrivateStorage && !(this.viewMode === 'wizard' && this.isUb);
  }

  public getManualBucketEnteringAlertText(): string {
    if (this._storageAccount.isPrivateStorage) {
      return this.i18N.transform('shared:modals.addDestinationBucket.privateStorageAlert', {
        name: this._storageAccount.displayName,
        type: this.bucketTypeName
      });
    }

    if (this.bucketsListingRestricted === BucketsListingRestrictedPossibility.Maybe) {
      return this.i18N.transform('shared:modals.addDestinationBucket.bucketsListingMaybeRestrictedAlert', {
        type: this.bucketTypeName,
        format: 'capitalize'
      });
    }

    if (this.bucketsListingRestricted === BucketsListingRestrictedPossibility.Exactly) {
      return this.i18N.transform('shared:modals.addDestinationBucket.bucketsListingExactlyRestrictedAlert', { type: this.bucketTypeName });
    }

    return '';
  }

  private unblockForm(): void {
    if (!this.blockForm) return;

    this.blockForm = false;
    this.criticalError.next(null);
    this.form?.enable({ emitEvent: false });
  }

  private onStorageAccountDefined(account: StorageAccountV2) {
    this.unblockForm();
    this.createNew.patchValue(true);
    this.bucketsLoaded = false;
    this.regions = [];
    !this.componentWasInitialized && this.defineControls();
    this._storageAccount = account;
    this.isUb = this._storageAccount.storageType === StorageType.UbAmazonS3 || this._storageAccount.storageType === StorageType.CblWasabi;

    of(account)
      .pipe(
        tap((account) => {
          account.isPrivateStorage && this.createNew.patchValue(false);
        }),
        switchMap(() => {
          return this.googleCloudAccounts.includes(this._storageAccount.storageType)
            ? this.storageAccountService
                .getGoogleStorageClasses(this.useDraftAccountPath)
                .pipe(tap(() => this.storageClassControl?.patchValue(null)))
            : of(null);
        }),
        tap((storageClasses) => (this.storageClasses = storageClasses)),
        switchMap(() => {
          return this._storageAccount.supportedBucketLocation && !this.googleCloudAccounts.includes(this._storageAccount.storageType)
            ? this.storageAccountService.getStorageAccountRegions({
                storageAccountId: this._storageAccount.id,
                fromWizard: this.useDraftAccountPath
              })
            : of(null);
        })
      )
      .subscribe((regions) => {
        this._storageAccount?.supportedBucketLocation && this.regionControl.setValidators([Validators.required]);
        this._storageAccount?.supportedS3Accelerator && this.s3AcceleratorControl.patchValue(false);
        !this.componentWasInitialized && this.addSubscriptions();

        if (this.destination) {
          this.updateFormByExistingDestination();
        } else {
          this.onCreateNewChanged(this.createNew?.value);
        }
        this.updateValidators();
        this.initialLoading.emit(false);
        this.regions = regions;
        this.componentWasInitialized = true;
      });
  }

  private defineControls() {
    this.regionField = this.form.get('region') as FormControl;
    this.storageClassField = this.form.get('storageClass') as FormControl;
    this.displayNameControl = this.form.get('displayBucketName') as FormControl;
    this.bucketNameControl = this.form.get('bucketName') as FormControl;
    this.storageClassControl = this.form.get('storageClass') as FormControl;
    this.regionControl = this.form.get('region') as FormControl;
    this.googleDestinationSubregionControl = this.form.get('googleDestinationSubregion') as FormControl;
    this.objectLockPeriodDaysControl = this.form.get('objectLockPeriodDays') as FormControl;
    this.gfsOLControl = this.form.get('isGfsObjectLockEnabled') as FormControl;
    this.companiesControl = this.form.get('companies') as FormControl;
    this.s3AcceleratorControl = this.form.get('s3Accelerator') as FormControl;
    this.reuseAccountRules = this.form.get('reuseAccountRules') as FormControl;
    this.isExternalBucketControl = this.form.get('isExternalBucket') as FormControl;
  }

  private addSubscriptions() {
    this.createNew.valueChanges.pipe(untilDestroyed(this)).subscribe((val) => this.onCreateNewChanged(val));
    this.subscribeToBucketNameChanged();
    this.subscribeToManualEnteredBucketNameChanged();
    this.createStorageClassFieldSubscription();
    this.createRegionFieldSubscription();
    this.setOLControlsSubscriptions();
    !this.blockForm && this.externalBucketSubscription();
  }

  private subscribeToBucketNameChanged() {
    this.bucketNameControl.valueChanges.pipe(untilDestroyed(this), distinctUntilChanged(), filter(Boolean)).subscribe((val) => {
      if (this.createNew.value || val === this.currentBucket()?.bucketName || this?.blockForm) return;

      if (this.isManualBucketEntering()) {
        return void this.currentBucketChanged.emit({
          bucketName: this.form.get('bucketName')?.value
        });
      }

      this.onBucketNameChanged(val);
    });
  }

  private subscribeToManualEnteredBucketNameChanged() {
    this.bucketNameControl.valueChanges
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        tap((val) => {
          if (this._storageAccount.isPrivateStorage) {
            this.currentBucketChanged.emit({
              bucketName: val
            });
          }
        }),
        filter(() => this.bucketsListingRestricted || this.isExternalBucketControl.value),
        debounceTime(1000)
      )
      .subscribe((val) => {
        if (!val) {
          this.currentBucket.set(null);
          this.currentBucketChanged.emit(null);

          return;
        }

        this.manualBucketLoading = true;
        this.onBucketNameChanged(val);
      });
  }

  private externalBucketSubscription() {
    this.isExternalBucketControl.valueChanges.subscribe((val) => {
      !this.createNew?.value &&
        this.bucketNameControl.patchValue(val ? null : this.currentBucket()?.bucketName || this.destination?.bucket?.bucketName || null);
    });
  }

  private onBucketNameChanged(bucketName: string) {
    this.currentBucket.set(null);
    this.currentBucketChanged.emit(null);
    this.bucketLoading.set(true);
    this.bucketLoadingChanged.emit(true);
    this.addQuery('bucketName');

    /*
      If we don't need additional params - just choose bucket info by bucket name.
      Sometimes bucket doesn't exist in the list, because it was deleted. To get valid error just mock bucket name itself.
    */
    const existingBucketInfo = (this.bucketsNames || []).find(({ bucketName: name }) => name === bucketName) || {
      bucketName
    };
    const request$ = this._storageAccount?.isPrivateStorage
      ? of(existingBucketInfo)
      : this.storageAccountService.getBucketInfo(this._storageAccount.id, bucketName, this.useDraftAccountPath);

    request$
      .pipe(
        finalize(() => {
          this.bucketLoading.set(false);
          this.bucketLoadingChanged.emit(false);
          this.manualBucketLoading = false;
          this.removeQuery('bucketName');
        })
      )
      .subscribe((res) => {
        this.currentBucket.set(res);

        this.currentBucketChanged.emit(this.currentBucket());
        this._storageAccount.supportedS3Accelerator && this.s3AcceleratorControl.patchValue(this.currentBucket()?.s3AcceleratorEnabled);
        this._storageAccount.isDefaultObjectLockSupportedForExisting &&
          this.currentBucket()?.objectLockPeriodDays !== null &&
          this.objectLockPeriodDaysControl.patchValue(this.currentBucket()?.objectLockPeriodDays);

        /*
          If OL is not supported by bucket need to switch it off, but not in the case where there are no rights to control Ol.
          In this case, we will get the expected conflict when saving. Also we need strong comparison with false, because in case of null
          we can't read from storage so we can't update OL
        */
        if (this.ability.can('read', PermissionsEnum.ManageImmutability) && (res as BucketInfo)?.immutabilitySupported === false) {
          if (this._storageAccount?.isGfsObjectLockSupported) {
            this.gfsOLControl.patchValue(false);
          }

          if (this._storageAccount?.isDefaultObjectLockSupportedForExisting) {
            this.objectLockPeriodDaysControl.patchValue(0);
          }
        }

        if (this.showDefaultOLMismatchModal()) {
          this.modalService
            .open(
              {
                header: {
                  title: this.i18N.transform('shared:modals.addDestinationBucket.olMismatchModalTitle')
                },
                footer: {
                  okButton: {
                    text: this.i18N.transform('shared:buttons.ok')
                  },
                  cancelButton: {
                    show: false
                  }
                }
              },
              this.olMismatch
            )
            .then();
        }
      });
  }

  private showDefaultOLMismatchModal() {
    const destBucketName = this.destination?.bucket?.bucketName;
    const destObjectLockPeriodDays = this.destination?.objectLockPeriodDays;
    const { bucketName, objectLockPeriodDays, immutabilitySupported } = this.currentBucket();

    return destBucketName === bucketName && destObjectLockPeriodDays !== objectLockPeriodDays && immutabilitySupported !== null;
  }

  private addQuery(queryName: string) {
    this.queries$.next([...this.queries$.value, queryName]);
  }

  private removeQuery(queryName: string) {
    this.queries$.next(this.queries$.value.filter((name) => name !== queryName));
  }

  private createStorageClassFieldSubscription() {
    this.storageClassField?.valueChanges.pipe(untilDestroyed(this), distinctUntilChanged()).subscribe((val) => {
      if (!this.createNew.value || !val?.id) return;

      this.specificGCDataLoading = true;
      this.addQuery('storageClass');

      if (val.id === GoogleStorageClassEnum.MultiRegional) {
        this.googleDestinationSubregionControl.disable();
      } else {
        this.googleDestinationSubregionControl.enable();
      }

      this.storageAccountService
        .getStorageAccountRegions({
          storageAccountId: this._storageAccount.id,
          googleStorageClass: val.id,
          fromWizard: this.useDraftAccountPath
        })
        .pipe(
          finalize(() => {
            this.specificGCDataLoading = false;
            this.removeQuery('storageClass');
          })
        )
        .subscribe((regions) => {
          this.regionField.patchValue(null);
          this.googleDestinationSubregionControl.patchValue(null);

          this.regions = regions;
          this.googleSubregions = null;
        });
    });
  }

  private createRegionFieldSubscription() {
    if (!this.googleCloudAccounts.includes(this._storageAccount.storageType)) return;

    this.regionField.valueChanges
      .pipe(
        untilDestroyed(this),
        filter((val) => Boolean(val)),
        distinctUntilChanged()
      )
      .subscribe((val) => {
        if (!this.createNew.value) return;

        this.specificGCDataLoading = true;
        this.addQuery('region');

        this.storageAccountService
          .getGoogleSubregions(val.systemName, this.storageClassField.value.id, this.useDraftAccountPath)
          .pipe(
            finalize(() => {
              this.specificGCDataLoading = false;
              this.removeQuery('region');
            })
          )
          .subscribe((subRegions) => {
            const subRegionsSystemNames = subRegions.map((subregion) => subregion.systemName);
            if (
              this.googleDestinationSubregionControl.value &&
              !subRegionsSystemNames.includes(this.googleDestinationSubregionControl.value?.systemName)
            ) {
              this.googleDestinationSubregionControl.patchValue(null);
            }

            this.googleSubregions = subRegions;
          });
      });
  }

  private updateFormByExistingDestination() {
    this.createNew.patchValue(false);
    this.form.patchValue({
      displayBucketName: this.destination.displayName,
      isGfsObjectLockEnabled: this.destination.isGfsObjectLockEnabled,
      objectLockPeriodDays: this.destination.objectLockPeriodDays
    });

    this.isExternalBucketControl.patchValue(this.destination?.isExternalBucket, { emitEvent: false });

    if (this.destination?.isReadOnly) {
      this.blockForm = true;
      this.form.patchValue({
        bucketName: this.destination?.bucket?.bucketName
      });
      this.form?.disable();
    } else {
      this.selectedCompanyIds = this.destination?.assignedCompanies || [];
    }
  }

  private updateValidators() {
    this._storageAccount.supportedBucketLocation && this.regionField.setValidators([Validators.required]);
    this._storageAccount.supportedStorageClass && this.createNew.value && this.storageClassField.setValidators([Validators.required]);
    this.googleCloudAccounts.includes(this._storageAccount.storageType) &&
      this.createNew.value &&
      this.googleDestinationSubregionControl.setValidators([Validators.required]);
    this.storageClassField?.updateValueAndValidity();
    this.googleDestinationSubregionControl?.updateValueAndValidity();
  }

  private onCreateNewChanged(val: boolean) {
    !(this.viewMode === 'wizard' && this.isUb) && this.bucketNameControl.reset();
    if (this.ability.can('read', PermissionsEnum.ManageImmutability)) {
      this.objectLockPeriodDaysControl.reset();
      this.gfsOLControl.reset();
    }

    if (val) {
      this.applyCreateNewBucket();
    } else {
      this.applyChooseExistingBucket();
    }

    this.regionField.updateValueAndValidity();
    this.googleDestinationSubregionControl.updateValueAndValidity();
    this.storageClassField.updateValueAndValidity();
  }

  private applyCreateNewBucket() {
    this._storageAccount.supportedBucketLocation && this.regionField.setValidators([Validators.required]);
    this._storageAccount.supportedStorageClass && this.storageClassField.setValidators([Validators.required]);
    this.googleCloudAccounts.includes(this._storageAccount.storageType) &&
      this.googleDestinationSubregionControl.setValidators([Validators.required]);
    this.isExternalBucketControl.patchValue(null);
    this.setBucketNameValidators();
  }

  private applyChooseExistingBucket() {
    this.regionField?.clearValidators();
    this.googleDestinationSubregionControl?.clearValidators();
    this.storageClassField?.clearValidators();
    this.bucketNameControl?.setValidators([Validators.required]);

    if (!this.bucketsLoaded && !this.isManualBucketEntering() && !this.blockForm) {
      this.loadBucketsList();
    } else {
      // in case of Manual Entering current bucket is unloaded
      const bucketName = this.currentBucket()?.bucketName || this.destination?.bucket?.bucketName;
      bucketName && this.bucketNameControl.patchValue(bucketName);
    }
  }

  private loadBucketsList() {
    this.bucketsLoading.set(true);
    this.addQuery('bucketList');
    this.storageAccountService
      .getBucketsNames({
        storageAccountId: this._storageAccount.id,
        fromWizard: this.useDraftAccountPath,
        skipError: true
      })
      .pipe(
        catchError((err) => {
          if (err?.error.typeCode === 4500) {
            this.bucketsListingRestricted = BucketsListingRestrictedPossibility.Exactly;

            return of(null);
          }

          this.criticalError.next(err?.error?.detail);
          this.blockForm = true;
          this.form?.disable();
          throw err;
        }),
        finalize(() => {
          this.bucketsLoading.set(false);
          this.bucketsLoaded = true;
          this.removeQuery('bucketList');
        })
      )
      .subscribe((res) => {
        this.bucketsNames = res;

        if (res && !res.length) {
          this.bucketsListingRestricted = BucketsListingRestrictedPossibility.Maybe;
        }

        if (this.destination) {
          this.bucketNameControl.patchValue(this.destination?.bucket?.bucketName);
        }
      });
  }
}
